import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { JourneyDots, Spinner } from '../../components/ui';
import { obsAPI, debriefAPI } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import { canObserve, canDebrief } from '../../lib/permissions';
import { toast } from 'react-toastify';
import { useObs } from '../../contexts/ObsContext';
import TabBar from '../../components/TabBar';
import { resumeFromHistory, isResumable } from '../../lib/resumeObservation';

/** What tapping an unfinished observation will do. */
const RESUME_LABEL = {
  in_progress:      'Continue observing →',
  obs_complete:     'Review evidence →',
  debrief_pending:  'Start debrief →',
  debrief_complete: 'Send feedback →',
};

export default function HistoryPage() {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resuming, setResuming] = useState(null);
  const ctx = useObs();
  const { user } = useAuth();

  // Resuming walks back into the observation flow, so it is only offered to
  // accounts that can actually do that work.
  const mayResume = canObserve(user) || canDebrief(user);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const handleResume = async (item) => {
    if (!isResumable(item.status)) {
      toast.info('This observation is complete — the feedback has already been sent.');
      return;
    }
    setResuming(item.id);
    try {
      const route = await resumeFromHistory(item, ctx, {
        notes:   id => obsAPI.notes(id),
        debrief: id => debriefAPI.get(id),
      });
      navigate(route);
    } catch (err) {
      console.error(err);
      toast.error('Could not reopen that observation. Please try again.');
    } finally {
      setResuming(null);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const response = await obsAPI.list();
      const data = response.data;
      const formattedHistory = (data.results ?? data)
        .sort((a, b) => new Date(b.started_at) - new Date(a.started_at)) // latest first
        .map((item, index) => {
        const initials = item.subject?.slice(0, 2)?.toUpperCase() || 'OB';
        const avatarStyles = [
          {
            avBg:'#EAF6FC',
            avColor:'#1A4A6B'
          },
          {
            avBg:'var(--amber-l)',
            avColor:'var(--amber-d)'
          },
          {
            avBg:'var(--purple-l)',
            avColor:'var(--purple-d)'
          },
        ];
        const style =
          avatarStyles[index % avatarStyles.length];
        return {
          // Keep the raw record — resuming needs the school, teacher and date,
          // not just the strings shown on the card.
          raw: item,
          id: item.id,
          name: `You Observed ${item.teacher_display_name || item.teacher_name}`,
          sub: `${item.school_name} · ${item.grade_level} · ${item.subject}`,
          initials,
          avBg: style.avBg,
          avColor: style.avColor,
          when: formatDate(item.started_at),
          status: item.status,
        };
      });
      setHistory(formattedHistory);
      setError('');
    } catch (err) {
      console.error('Error fetching history:', err);
      setError('Could not load your history. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
  const date = new Date(dateString);
  const today = new Date();
  const isToday = date.toDateString() === today.toDateString();
  if (isToday) {
      return 'Today';
    }
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
    });
  };

  return (
    <div className="screen">
      <div className="obsbar">
        <button className="obsbar-back" onClick={() => navigate('/')}>← Back</button>
        <div className="obsbar-title">History</div>
      </div>

      <div className="scroll">
        <div className="pad">
          <div className="sec-lbl" style={{ marginTop:0 }}>Most recent first</div>

          {loading && (
            <div style={{ display:'flex', justifyContent:'center', padding:'48px 0' }}>
              <Spinner />
            </div>
          )}

          {!loading && error && <div className="dash-empty">{error}</div>}

          {!loading && !error && history.length === 0 && (
            <div className="dash-empty">No observations yet.</div>
          )}

          {!loading && !error && history.map((item, i) => {
            const resumable = mayResume && isResumable(item.status);
            return (
              <div
                key={item.id}
                className={`hist-item animate-fade-up${resumable ? ' resumable' : ''}`}
                style={{ animationDelay:`${i*50}ms` }}
                role={resumable ? 'button' : undefined}
                tabIndex={resumable ? 0 : undefined}
                onClick={resumable ? () => handleResume(item.raw) : undefined}
                onKeyDown={resumable ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleResume(item.raw); }
                } : undefined}
              >
                <div className="hist-row" style={{ marginBottom:4 }}>
                  <div className="hist-av" style={{ background: item.avBg, color: item.avColor }}>{item.initials}</div>
                  <div style={{ flex:1 }}>
                    <div className="hist-name">{item.name}</div>
                    <div style={{ fontSize:11, color:'var(--text2)' }}>{item.sub}</div>
                  </div>
                  <div className="hist-time">{item.when}</div>
                </div>
                <JourneyDots status={item.status} />
                {resumable && (
                  <div className="hist-resume">
                    {resuming === item.id ? 'Reopening…' : RESUME_LABEL[item.status]}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <TabBar current="history" />
    </div>
  );
}
